import { Navigate, Outlet } from "react-router-dom";
import { ROUTES } from "@/app/constants";
import type { UserRole } from "@/features/auth/types";
import { useAuthStore } from "@/stores/authStore";
import { pickHomeForRole } from "@/app/router/roleHome";

interface RoleGuardProps {
  allowedRoles: UserRole[];
}

/**
 * Restricts a route subtree to a set of roles.
 *
 * Fallback deliberately routes to the *user's own role-appropriate home*
 * via `pickHomeForRole`, not `/dashboard`. The previous "always redirect
 * to /dashboard" behavior meant a CLIENT who tried to open an admin route
 * bounced right back into the admin shell (because `/dashboard` itself
 * had no role gate at the time). Now denials land somewhere the user is
 * actually allowed to be, or on `/login` if not signed in.
 */
export function RoleGuard({ allowedRoles }: RoleGuardProps) {
  const { user } = useAuthStore();

  if (!user) return <Navigate to={ROUTES.login} replace />;

  if (!allowedRoles.includes(user.role)) {
    return <Navigate to={pickHomeForRole(user.role)} replace />;
  }

  return <Outlet />;
}
